import { useEffect, useRef, useState, useCallback } from "react";
import { X, Settings, Image as ImageIcon, Trash2, Crop, Check } from "lucide-react";
import { api } from "../lib/api";

/**
 * WallpaperCropper — inline canvas-based crop UI.
 * Shows the full image; user drags to define crop rect; Confirm outputs
 * a data URL of just the selected region at original image resolution.
 */
export function WallpaperCropper({ src, onConfirm, onCancel }) {
  const canvasRef = useRef(null);
  const imgRef = useRef(null);
  const [drag, setDrag] = useState(null); // { startX, startY, x, y, w, h }
  const [rect, setRect] = useState(null); // final crop rect in canvas coords
  const [imgLoaded, setImgLoaded] = useState(false);
  const [displaySize, setDisplaySize] = useState({ w: 0, h: 0 });

  // Load image and draw to canvas
  useEffect(() => {
    const img = new Image();
    img.onload = () => {
      imgRef.current = img;
      const maxW = Math.min(760, window.innerWidth - 64);
      const maxH = Math.min(480, window.innerHeight - 200);
      const scale = Math.min(maxW / img.naturalWidth, maxH / img.naturalHeight, 1);
      const dW = Math.round(img.naturalWidth * scale);
      const dH = Math.round(img.naturalHeight * scale);
      setDisplaySize({ w: dW, h: dH });
      setRect({ x: 0, y: 0, w: dW, h: dH }); // default = full image
      setImgLoaded(true);
    };
    img.src = src;
  }, [src]);

  // Redraw whenever rect changes
  useEffect(() => {
    const canvas = canvasRef.current;
    const img = imgRef.current;
    if (!canvas || !img || !imgLoaded) return;
    const ctx = canvas.getContext("2d");
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
    if (rect) {
      // Dim outside crop rect
      ctx.fillStyle = "rgba(0,0,0,0.55)";
      ctx.fillRect(0, 0, canvas.width, rect.y);
      ctx.fillRect(0, rect.y + rect.h, canvas.width, canvas.height - rect.y - rect.h);
      ctx.fillRect(0, rect.y, rect.x, rect.h);
      ctx.fillRect(rect.x + rect.w, rect.y, canvas.width - rect.x - rect.w, rect.h);
      // Crop border
      ctx.strokeStyle = "#5A90F5";
      ctx.lineWidth = 2;
      ctx.setLineDash([6, 3]);
      ctx.strokeRect(rect.x + 1, rect.y + 1, rect.w - 2, rect.h - 2);
      // Corner handles
      const H = 10;
      ctx.setLineDash([]);
      ctx.strokeStyle = "#fff";
      ctx.lineWidth = 2;
      [[rect.x, rect.y], [rect.x + rect.w, rect.y], [rect.x, rect.y + rect.h], [rect.x + rect.w, rect.y + rect.h]].forEach(([cx, cy]) => {
        ctx.strokeRect(cx - H / 2, cy - H / 2, H, H);
      });
    }
  }, [rect, imgLoaded, displaySize]);

  const getCanvasXY = (e) => {
    const c = canvasRef.current;
    const r = c.getBoundingClientRect();
    return { x: e.clientX - r.left, y: e.clientY - r.top };
  };

  const onMouseDown = (e) => {
    const { x, y } = getCanvasXY(e);
    setDrag({ startX: x, startY: y, x, y, w: 0, h: 0 });
  };
  const onMouseMove = (e) => {
    if (!drag) return;
    const { x, y } = getCanvasXY(e);
    const rx = Math.min(drag.startX, x);
    const ry = Math.min(drag.startY, y);
    const rw = Math.abs(x - drag.startX);
    const rh = Math.abs(y - drag.startY);
    setRect({ x: rx, y: ry, w: Math.max(rw, 10), h: Math.max(rh, 10) });
  };
  const onMouseUp = () => setDrag(null);

  const confirm = () => {
    const img = imgRef.current;
    const canvas = canvasRef.current;
    if (!img || !canvas || !rect) return;
    // Scale rect from display coords to natural image coords
    const scaleX = img.naturalWidth / canvas.width;
    const scaleY = img.naturalHeight / canvas.height;
    const cx = Math.round(rect.x * scaleX);
    const cy = Math.round(rect.y * scaleY);
    const cw = Math.round(rect.w * scaleX);
    const ch = Math.round(rect.h * scaleY);
    const out = document.createElement("canvas");
    // Cap at 1920x1080 — wallpapers don't need 4K, and keeping it small prevents
    // localStorage quota errors and CSS parsing freezes on body.style.background.
    const MAX_W = 1920, MAX_H = 1080;
    const scale = Math.min(1, MAX_W / cw, MAX_H / ch);
    out.width  = Math.round(cw * scale);
    out.height = Math.round(ch * scale);
    out.getContext("2d").drawImage(img, cx, cy, cw, ch, 0, 0, out.width, out.height);
    onConfirm(out.toDataURL("image/jpeg", 0.75));
  };

  return (
    <div className="fixed inset-0 z-[500] bg-black/80 backdrop-blur-sm flex flex-col items-center justify-center gap-4 p-6">
      <div className="bg-[#141414] border border-[#2A2A2A] rounded-xl p-5 flex flex-col gap-4 max-w-[800px] w-full">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 text-sm font-semibold text-[#EDEDED]">
            <Crop className="w-4 h-4 text-[#5A90F5]" />
            Crop Wallpaper
          </div>
          <button onClick={onCancel} className="p-1 rounded hover:bg-[#1a1a1a] text-[#A1A1AA]"><X className="w-4 h-4" /></button>
        </div>
        <p className="text-[11px] text-[#71717A]">Drag on the image to select the area you want to use. The default selection uses the full image.</p>
        {imgLoaded ? (
          <canvas
            ref={canvasRef}
            width={displaySize.w}
            height={displaySize.h}
            className="rounded border border-[#2A2A2A] cursor-crosshair select-none"
            style={{ maxWidth: "100%" }}
            onMouseDown={onMouseDown}
            onMouseMove={onMouseMove}
            onMouseUp={onMouseUp}
            onMouseLeave={onMouseUp}
          />
        ) : (
          <div className="h-48 flex items-center justify-center text-[#52525B] text-xs">Loading…</div>
        )}
        <div className="flex items-center justify-end gap-2">
          <button onClick={onCancel} className="px-4 py-2 text-xs text-[#71717A] hover:text-[#EDEDED]">Cancel</button>
          <button
            onClick={confirm}
            className="px-4 py-2 text-xs font-semibold text-white rounded-lg flex items-center gap-1.5"
            style={{ background: "#5A90F5" }}
          >
            <Check className="w-3.5 h-3.5" /> Use This Crop
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * CalendarSettings — modal panel triggered by the cogwheel button in the
 * calendar header. iter32: bundles all calendar-level prefs in one
 * place: default zoom, day tints, snap-to-grid (legacy), and a custom
 * background image with dim + liquid-glass tuning.
 *
 * All settings persist to localStorage and are applied via a small CSS
 * variable handshake on `document.documentElement` so any DOM that
 * reads `--cresc-cal-bg-*` picks them up live.
 */
const KEYS = {
  default_zoom: "crescendo:colZoom",
  day_tints: "crescendo:dayTints",
  bg_url: "crescendo:calBgUrl",
  bg_dim: "crescendo:calBgDim",
  bg_blur: "crescendo:calBgBlur",
};

export function applyCalendarBgVars() {
  // Read from localStorage and push to CSS variables. Called once at
  // boot (App.js) and whenever the settings panel saves.
  const url = localStorage.getItem(KEYS.bg_url) || "";
  const dim = parseFloat(localStorage.getItem(KEYS.bg_dim) || "0.85");
  const blur = parseFloat(localStorage.getItem(KEYS.bg_blur) || "12");
  const root = document.documentElement;
  root.style.setProperty("--cresc-cal-bg-url", url ? `url("${url}")` : "none");
  root.style.setProperty("--cresc-cal-bg-dim", String(Math.max(0, Math.min(1, dim))));
  root.style.setProperty("--cresc-cal-bg-blur", `${Math.max(0, Math.min(40, blur))}px`);
  // Iter35 user request: "On the background, it needs to mask underneath
  // the ENTIRE screen, navbar, sidebar, and all." Mirror the same vars
  // to a body-level pseudo-element so the bg shows through behind the
  // sidebar + topbar too. Body class toggles whether the global layer
  // is rendered at all.
  root.style.setProperty("--cresc-app-bg-url", url ? `url("${url}")` : "none");
  // Slightly heavier blur + dim on the app-wide layer so chrome stays
  // legible — readability over fidelity.
  const appDim = Math.max(0.85, Math.min(1, dim + 0.05));
  const appBlur = Math.max(blur + 6, 14);
  root.style.setProperty("--cresc-app-bg-dim", String(appDim));
  root.style.setProperty("--cresc-app-bg-blur", `${appBlur}px`);
  document.body.classList.toggle("cresc-has-bg", !!url);
}

export function CalendarSettingsButton() {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="w-7 h-7 inline-flex items-center justify-center rounded border border-[#2A2A2A] hover:border-[#5A90F5] text-[#A1A1AA] hover:text-[#EDEDED]"
        title="Calendar settings"
        data-testid="calendar-settings-btn"
      >
        <Settings className="w-3.5 h-3.5" />
      </button>
      {open && <CalendarSettingsModal onClose={() => setOpen(false)} />}
    </>
  );
}

function CalendarSettingsModal({ onClose }) {
  const [zoom, setZoom] = useState(() => parseFloat(localStorage.getItem(KEYS.default_zoom) || "1") || 1);
  const [dayTints, setDayTints] = useState(() => localStorage.getItem(KEYS.day_tints) !== "0");
  const [bgUrl, setBgUrl] = useState(() => localStorage.getItem(KEYS.bg_url) || "");
  const [dim, setDim] = useState(() => parseFloat(localStorage.getItem(KEYS.bg_dim) || "0.85"));
  const [blur, setBlur] = useState(() => parseFloat(localStorage.getItem(KEYS.bg_blur) || "12"));
  const [cropSrc, setCropSrc] = useState(null); // pending crop — raw data URL before crop

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const save = () => {
    localStorage.setItem(KEYS.default_zoom, String(zoom));
    localStorage.setItem(KEYS.day_tints, dayTints ? "1" : "0");
    localStorage.setItem(KEYS.bg_url, bgUrl);
    localStorage.setItem(KEYS.bg_dim, String(dim));
    localStorage.setItem(KEYS.bg_blur, String(blur));
    applyCalendarBgVars();
    // Sync wallpaper to server so it's available on all devices
    api.put("/me/background", { url: bgUrl, dim, blur }).catch(() => {});
    window.dispatchEvent(new CustomEvent("crescendo:calsettings-changed"));
    // Broadcast new bg so WallpaperLayer updates immediately
    window.dispatchEvent(new CustomEvent("cresc-bg-changed", { detail: { url: bgUrl, dim, blur } }));
    onClose();
  };

  // Iter39 user request: wallpaper size requirements + 8K support.
  const onPickImage = (e) => {
    const file = e.target.files?.[0];
    e.target.value = ""; // reset so same file can be re-picked
    if (!file) return;
    const MAX_MB = 32; // support up to 8K (typically ~10-20MB JPEG)
    if (file.size > MAX_MB * 1024 * 1024) {
      window.alert(`Image must be under ${MAX_MB} MB.`);
      return;
    }
    const reader = new FileReader();
    reader.onload = (ev) => {
      const dataUrl = String(ev.target.result || "");
      // Open crop UI — user selects region before committing
      setCropSrc(dataUrl);
    };
    reader.readAsDataURL(file);
  };

  return (
    <>
    {cropSrc && (
      <WallpaperCropper
        src={cropSrc}
        onConfirm={(cropped) => { setBgUrl(cropped); setCropSrc(null); }}
        onCancel={() => setCropSrc(null)}
      />
    )}
    <div
      className="fixed inset-0 z-[200] bg-black/60 flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="calendar-settings-modal"
    >
      <div
        className="bg-[#141414] border border-[#2A2A2A] rounded-xl shadow-2xl w-full max-w-md p-5"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Settings className="w-4 h-4 text-[#5A90F5]" />
            <h3 className="font-display text-sm font-semibold">Calendar Settings</h3>
          </div>
          <button onClick={onClose} className="p-1 rounded hover:bg-[#1a1a1a] text-[#A1A1AA]">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="space-y-4">
          <div>
            <label className="meta-label block mb-1">Default Column Zoom — {Math.round(zoom * 100)}%</label>
            <input
              type="range" min={0.5} max={3} step={0.25}
              value={zoom}
              onChange={(e) => setZoom(parseFloat(e.target.value))}
              className="w-full"
              data-testid="calsettings-default-zoom"
            />
          </div>

          <label className="flex items-center justify-between cursor-pointer text-xs text-[#EDEDED]">
            <span>Day Tints (subtle weekday color stripes)</span>
            <input
              type="checkbox"
              checked={dayTints}
              onChange={(e) => setDayTints(e.target.checked)}
              data-testid="calsettings-day-tints"
            />
          </label>

          <div className="border-t border-[#2A2A2A] pt-4">
            <label className="meta-label block mb-2">Custom Background</label>
            <div className="flex items-center gap-2 mb-2">
              <label className="flex items-center gap-1.5 px-2 py-1 rounded border border-[#3A3A3A] bg-[#0A0A0A] cursor-pointer hover:border-[#5A90F5] text-[11px]">
                <ImageIcon className="w-3 h-3" /> Upload
                <input type="file" accept="image/*" onChange={onPickImage} className="hidden" data-testid="calsettings-bg-upload" />
              </label>
              {bgUrl && (
                <button
                  type="button"
                  onClick={() => setBgUrl("")}
                  className="px-2 py-1 rounded border border-[#3A3A3A] text-[#FF3B30] hover:bg-[#FF3B30]/10 text-[11px] inline-flex items-center gap-1"
                  data-testid="calsettings-bg-clear"
                >
                  <Trash2 className="w-3 h-3" /> Clear
                </button>
              )}
            </div>
            <input
              type="url"
              placeholder="…or paste an image URL"
              value={bgUrl}
              onChange={(e) => setBgUrl(e.target.value)}
              className="w-full bg-[#0A0A0A] border border-[#2A2A2A] rounded px-2 py-1 text-[11px] text-[#EDEDED] outline-none focus:border-[#5A90F5]"
              data-testid="calsettings-bg-url"
            />

            {bgUrl && (
              <div className="mt-3 space-y-2">
                <div className="relative h-20 rounded-md border border-[#2A2A2A] overflow-hidden">
                  <div className="absolute inset-0" style={{
                    backgroundImage: `url("${bgUrl}")`,
                    backgroundSize: "cover",
                    backgroundPosition: "center",
                    filter: `blur(${blur}px) brightness(${1 - dim})`,
                  }} />
                  <div className="absolute inset-0 bg-white/5 backdrop-saturate-150 mix-blend-overlay" />
                  <div className="absolute inset-0 flex items-center justify-center text-[10px] uppercase tracking-wider text-[#A1A1AA] backdrop-blur-sm">Preview</div>
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-wider text-[#A1A1AA] block mb-1">Dim — {Math.round(dim * 100)}%</label>
                  <input
                    type="range" min={0} max={1} step={0.05}
                    value={dim} onChange={(e) => setDim(parseFloat(e.target.value))}
                    className="w-full"
                    data-testid="calsettings-bg-dim"
                  />
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-wider text-[#A1A1AA] block mb-1">Liquid Glass Blur — {blur}px</label>
                  <input
                    type="range" min={0} max={40} step={1}
                    value={blur} onChange={(e) => setBlur(parseFloat(e.target.value))}
                    className="w-full"
                    data-testid="calsettings-bg-blur"
                  />
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="flex justify-end gap-2 mt-5">
          <button
            onClick={onClose}
            className="text-[11px] px-3 py-1.5 rounded border border-[#2A2A2A] text-[#A1A1AA] hover:bg-[#1a1a1a]"
            data-testid="calsettings-cancel"
          >Cancel</button>
          <button
            onClick={save}
            className="text-[11px] px-3 py-1.5 rounded bg-[#5A90F5] text-white font-semibold hover:bg-[#4a80e5]"
            data-testid="calsettings-save"
          >Save</button>
        </div>
      </div>
    </div>
    </>
  );
}
